import { components as c } from "@/components";
import {
    searchForTemplateAiWizardNavigation,
    useTemplateAiWizardSearch,
} from "@/features/ai-builder/useTemplateAiDraftStore";
import { formComponents as fc } from "@/features/i18n/formComponents";
import { StaffUserComboBox } from "@/features/profile/components/StaffUserComboBox";
import useInjectionOptions from "@/features/injection/hooks/useInjectionOptions";
import InviteForm from "@/features/invite/components/InviteForm";
import { LockableField, LockableFieldLabel } from "@/features/templates/components/TemplateScopeLock";
import { TemplateStepFooter } from "@/features/templates/components/TemplateStepFooter";
import { useTemplateLocks } from "@/features/templates/hooks/useTemplateLocks";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { templateSettingsSchema } from "@patient-watch/types";
import { isUuid } from "@/services/isUuid";
import { services as s } from "@/services";
import { useAuth } from "@/utils/auth";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

const injectionFormSchema = z.object({
    referring_clinician_id: z.string().optional().nullable(),
    injecting_clinician_id: z.string().optional().nullable(),
    injection_method_id: z.string().optional().nullable(),
});

type InjectionFormValues = z.infer<typeof injectionFormSchema>;

const InjectionFormFieldsPage = () => {
    const auth = useAuth();
    const { templateId } = Route.useParams();
    const locks = useTemplateLocks(templateId);
    const referringLocked = locks.isLocked("referring_clinician_id");
    const injectingLocked = locks.isLocked("injecting_clinician_id");
    const search = useTemplateAiWizardSearch(templateId, Route.useSearch());

    const referring_clinician_id = search.referring_clinician_id;
    const injecting_clinician_id = search.injecting_clinician_id;
    const injection_method_id = search.injection_method_id;

    const { markStepComplete, nextStep, setCurrentStep, getNextStepRoute, getPreviousStepRoute, currentStep } =
        useTemplateNavigationStore();
    const navigate = useNavigate();

    const form = useForm<InjectionFormValues>({
        resolver: zodResolver(injectionFormSchema),
        defaultValues: {
            referring_clinician_id: "",
            injecting_clinician_id: "",
            injection_method_id: "",
        },
    });

    const { methodOptions, clinicianOptions } = useInjectionOptions();

    useEffect(() => {
        setCurrentStep("injection");
    }, [setCurrentStep]);

    // Set form values after options load - using reset to ensure ComboBox recognizes values
    useEffect(() => {
        if (clinicianOptions.length > 0) {
            // Set form values from search params or default to user's own ID
            const referringValue = isUuid(referring_clinician_id)
                ? referring_clinician_id
                : referringLocked
                  ? ""
                  : auth?.id || "";
            const injectingValue = isUuid(injecting_clinician_id)
                ? injecting_clinician_id
                : injectingLocked
                  ? ""
                  : auth?.id || "";

            form.reset({
                referring_clinician_id: referringValue,
                injecting_clinician_id: injectingValue,
                injection_method_id: injection_method_id || "",
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        clinicianOptions.length,
        referring_clinician_id,
        injecting_clinician_id,
        injection_method_id,
        auth?.id,
        referringLocked,
        injectingLocked,
    ]);

    const handleNext = (data: InjectionFormValues) => {
        markStepComplete(currentStep);
        nextStep();
        const nextRoute = getNextStepRoute(currentStep, { templateId });
        if (nextRoute) {
            const nextSettings = {
                ...search,
                ...data,
                injection_id: search.injection_id || s.uuidv4(),
            };
            const wizardSearchPayload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
            if (!wizardSearchPayload) return;
            void navigate({
                ...nextRoute,
                search: wizardSearchPayload,
            });
        }
    };

    const handlePrevious = () => {
        const prevRoute = getPreviousStepRoute(currentStep, { templateId });
        if (prevRoute) {
            const nextSettings = { ...search, ...form.getValues() };
            const wizardSearchPayload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
            if (!wizardSearchPayload) return;
            void navigate({
                ...prevRoute,
                search: wizardSearchPayload,
            });
        }
    };

    return (
        <c.ui.Form {...form}>
            <form onSubmit={form.handleSubmit(handleNext)} className="space-y-6">
                <div className="space-y-6">
                    <LockableField field="referring_clinician_id">
                        <LockableFieldLabel htmlFor="referring_clinician_id">Referring Clinician</LockableFieldLabel>
                        <StaffUserComboBox
                            autoFocus
                            name="referring_clinician_id"
                            description="The clinician who requests the injection"
                            options={clinicianOptions}
                            clearable={true}
                            addOption={<InviteForm />}
                        />
                    </LockableField>
                    <LockableField field="injecting_clinician_id">
                        <LockableFieldLabel htmlFor="injecting_clinician_id">Injecting Clinician</LockableFieldLabel>
                        <StaffUserComboBox
                            name="injecting_clinician_id"
                            description="The clinician who performs the injection"
                            options={clinicianOptions}
                            clearable={true}
                            addOption={<InviteForm />}
                        />
                    </LockableField>
                    <LockableField field="injection_method_id">
                        <fc.ComboBox
                            name="injection_method_id"
                            label="Injection Method"
                            description="How and where injection is performed"
                            options={methodOptions}
                            clearable={true}
                        />
                    </LockableField>
                </div>

                <TemplateStepFooter onBack={handlePrevious} submitPrimary />
            </form>
        </c.ui.Form>
    );
};

const injectionSearchSchema = templateSettingsSchema.partial().pick({
    referring_clinician_id: true,
    injecting_clinician_id: true,
    injection_method_id: true,
    injection_id: true,
});

export const Route = createFileRoute("/_app/templates/$templateId/events/injection/")({
    component: InjectionFormFieldsPage,
    validateSearch: (search) => injectionSearchSchema.parse(search),
});
